<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import CurrencyInput from 'dashboard/components-next/input/CurrencyInput.vue';
import { usePaymentMethods } from 'dashboard/composables/usePaymentMethods';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  isSending: { type: Boolean, default: false },
  quoteTemplates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'send',
  'sendQuote',
  'generateQuotePdf',
  'generateReceipt',
]);

const copy = {
  contractTitle: 'Enviar contrato',
  quoteTitle: 'Enviar orçamento',
  receiptTitle: 'Enviar recibo de pagamento',
  receipt: 'Recibo de pagamento',
  quote: 'Orçamento',
  receivedAmount: 'Valor recebido (R$)',
  receivedOn: 'Data do recebimento',
  paymentMethod: 'Forma de pagamento',
  confirmPayment: 'Confirmo que este valor foi recebido.',
  receiptHint:
    'Preencha os dados do pagamento confirmado. Escolha PDF ou imagem para enviar junto à mensagem nesta conversa.',
  contractDescription:
    'Selecione a caixa e, se desejar, um template para enviar o link.',
  quoteDescription:
    'Selecione a caixa e o modelo para enviar o orçamento ao contato.',
  inbox: 'Caixa de entrada',
  template: 'Modelo de mensagem',
  destination: '1. Escolha o destino',
  review: '2. Revise o orçamento',
  recipient: 'Destinatário',
  preview: 'Mensagem de acompanhamento',
  previewHint:
    'O modelo padrão convida o cliente a tirar dúvidas ou pedir ajustes.',
  defaultDescription:
    'Resumo dos itens, total e condições, com convite para continuar a conversa.',
  sendHint:
    'Confira os dados antes de enviar. O orçamento será enviado na conversa deste cliente.',
  selectInbox: 'Selecione uma caixa de entrada',
  reviewItems: 'Conferir itens',
  noTemplate: 'Enviar mensagem simples',
  quoteTemplate: 'Modelo de orçamento',
  defaultQuoteTemplate: 'Modelo padrão',
  cancel: 'Cancelar',
  confirm: 'Enviar',
  generatePdf: 'Gerar PDF',
  copySignatureLink: 'Copiar link de assinatura',
  noInbox: 'Nenhuma caixa disponível para envio.',
  customer: 'Cliente',
  document: 'CPF/CNPJ',
  phone: 'Telefone',
  items: 'Itens',
  total: 'Total',
  sendFormat: 'Formato do envio',
  sendFormatText: 'Somente mensagem',
  sendFormatTextDetail: 'Itens e total no texto',
  sendFormatPdf: 'PDF em anexo',
  sendFormatPdfDetail: 'Documento A4 com o modelo',
  sendFormatPng: 'Imagem em anexo',
  sendFormatPngDetail: 'PNG com o modelo',
  sendFormatHint:
    'O anexo usa o mesmo documento do botão Gerar PDF e segue com uma mensagem curta.',
  emailRecipient: 'Destinatário do e-mail',
  contactEmail: 'E-mail do contato',
  manualEmail: 'Outro e-mail',
  emailAddress: 'E-mail do destinatário',
  emailPlaceholder: 'nome@empresa.com.br',
  ccEmails: 'Cópia (CC)',
  ccPlaceholder: 'financeiro@empresa.com.br, gerente@empresa.com.br',
  ccHint: 'Opcional. Separe vários e-mails por vírgula (até 10).',
  loadingContactEmail: 'Carregando e-mail do contato…',
  noContactEmail:
    'Este contato não tem e-mail cadastrado. Informe o e-mail do destinatário.',
  invalidEmail: 'Informe um e-mail válido.',
  invalidCc: 'Confira os e-mails em cópia:',
  tooManyCc: 'Informe no máximo 10 e-mails em cópia.',
  emailConversationHint:
    'Se o contato ainda não tiver conversa nesta caixa, uma nova conversa de e-mail será iniciada.',
};

const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const MAX_CC_EMAILS = 10;

// Ícones por tipo de conexão: Simple Icons só para as marcas autorizadas.
const CHANNEL_ICONS = {
  'Channel::Whatsapp': 'i-simple-icons-whatsapp',
  'Channel::Email': 'i-hugeicons-mail-01',
  'Channel::Instagram': 'i-simple-icons-instagram',
  'Channel::FacebookPage': 'i-simple-icons-meta',
  'Channel::Telegram': 'i-simple-icons-telegram',
  'Channel::Tiktok': 'i-simple-icons-tiktok',
  'Channel::WebWidget': 'i-hugeicons-internet',
  'Channel::Api': 'i-hugeicons-api',
  'Channel::Sms': 'i-hugeicons-message-01',
  'Channel::TwitterProfile': 'i-hugeicons-new-twitter',
};

const inboxIcon = inbox => {
  const type = inbox.channel_type || inbox.channelType;
  if (type === 'Channel::TwilioSms') {
    return inbox.medium === 'whatsapp'
      ? CHANNEL_ICONS['Channel::Whatsapp']
      : CHANNEL_ICONS['Channel::Sms'];
  }
  return CHANNEL_ICONS[type] || 'i-hugeicons-chatting-01';
};

const parseEmails = value =>
  String(value || '')
    .split(/[,;\s]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean);

const dialogRef = ref(null);
const contract = ref(null);
const quotePayload = ref(null);
const form = reactive({
  inboxId: '',
  templateIndex: '',
  quoteTemplateId: '',
  documentKind: 'quote',
  receiptFormat: 'pdf',
  receivedAmount: '',
  receivedOn: '',
  paymentMethod: '',
  paymentConfirmed: false,
  quoteAttachmentFormat: '',
  emailMode: 'contact',
  toEmail: '',
  ccEmails: '',
});

const contactEmail = ref('');
const isLoadingContactEmail = ref(false);
let contactEmailRequestId = 0;

const { paymentMethodOptions } = usePaymentMethods();

const inboxes = useMapGetter('inboxes/getInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => Number(inbox.id) === Number(form.inboxId))
);

const templates = computed(() =>
  form.inboxId ? getFilteredWhatsAppTemplates.value(form.inboxId) || [] : []
);

const selectedTemplate = computed(() =>
  form.templateIndex !== '' ? templates.value[Number(form.templateIndex)] : null
);

const activeQuoteTemplates = computed(() =>
  props.quoteTemplates.filter(template => template.active !== false)
);

const isQuoteMode = computed(() => !!quotePayload.value);
const isReceipt = computed(
  () => isQuoteMode.value && form.documentKind === 'receipt'
);
const isEmailInbox = computed(
  () =>
    isQuoteMode.value &&
    (selectedInbox.value?.channel_type || selectedInbox.value?.channelType) ===
      'Channel::Email'
);
const emailRecipient = computed(() =>
  form.emailMode === 'contact' && contactEmail.value
    ? contactEmail.value
    : form.toEmail.trim().toLowerCase()
);
const ccList = computed(() =>
  [...new Set(parseEmails(form.ccEmails))].filter(
    email => email !== emailRecipient.value
  )
);
const invalidCcEmails = computed(() =>
  ccList.value.filter(email => !EMAIL_PATTERN.test(email))
);
const isRecipientEmailInvalid = computed(
  () => !EMAIL_PATTERN.test(emailRecipient.value)
);
const ccError = computed(() => {
  if (ccList.value.length > MAX_CC_EMAILS) return copy.tooManyCc;
  if (invalidCcEmails.value.length) {
    return `${copy.invalidCc} ${invalidCcEmails.value.join(', ')}`;
  }
  return '';
});
const isEmailInvalid = computed(
  () =>
    isEmailInbox.value &&
    (isLoadingContactEmail.value ||
      isRecipientEmailInvalid.value ||
      Boolean(ccError.value))
);
const quoteFormatOptions = [
  {
    value: '',
    label: copy.sendFormatText,
    detail: copy.sendFormatTextDetail,
    icon: 'i-hugeicons-message-01',
  },
  {
    value: 'pdf',
    label: copy.sendFormatPdf,
    detail: copy.sendFormatPdfDetail,
    icon: 'i-hugeicons-file-01',
  },
  {
    value: 'png',
    label: copy.sendFormatPng,
    detail: copy.sendFormatPngDetail,
    icon: 'i-hugeicons-image-01',
  },
];
const emailModeOptions = computed(() => [
  {
    value: 'contact',
    label: copy.contactEmail,
    detail: contactEmail.value,
    disabled: !contactEmail.value,
  },
  { value: 'manual', label: copy.manualEmail, detail: '', disabled: false },
]);

const quoteItems = computed(() => quotePayload.value?.invoice_items || []);
const quoteTotal = computed(() =>
  quoteItems.value.reduce((total, item) => {
    const quantity = Number(item.quantity) > 0 ? Number(item.quantity) : 1;
    return total + quantity * Number(item.unit_price || 0);
  }, 0)
);
const formattedQuoteTotal = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(quoteTotal.value)
);
const title = computed(() => {
  if (isReceipt.value) return copy.receiptTitle;
  return isQuoteMode.value ? copy.quoteTitle : copy.contractTitle;
});
const description = computed(() => {
  if (isReceipt.value) return copy.receiptHint;
  return isQuoteMode.value ? copy.quoteDescription : copy.contractDescription;
});
const isReceiptInvalid = computed(
  () =>
    !form.paymentConfirmed ||
    !Number.isFinite(Number(form.receivedAmount)) ||
    Number(form.receivedAmount) <= 0 ||
    !form.receivedOn ||
    !form.paymentMethod.trim()
);
const receiptPayload = () => ({
  ...quotePayload.value,
  receipt_format: form.receiptFormat,
  received_amount: form.receivedAmount,
  received_on: form.receivedOn,
  payment_method: form.paymentMethod,
  payment_confirmed: form.paymentConfirmed,
});
const handleGenerateReceipt = format => {
  if (props.isSending || isReceiptInvalid.value || !quotePayload.value) return;
  form.receiptFormat = format;
  emit('generateReceipt', receiptPayload());
};
const isConfirmDisabled = computed(
  () =>
    props.isSending ||
    !form.inboxId ||
    (!contract.value && !quotePayload.value) ||
    isEmailInvalid.value ||
    (isReceipt.value &&
      (!form.paymentConfirmed ||
        !Number.isFinite(Number(form.receivedAmount)) ||
        Number(form.receivedAmount) <= 0 ||
        !form.receivedOn ||
        !form.paymentMethod.trim()))
);

const templateLabel = template => template.name || template.friendly_name;
const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name || inbox.channel_type,
    icon: inboxIcon(inbox),
  }))
);
const quoteOptions = computed(() => [
  { value: '', label: copy.defaultQuoteTemplate },
  ...activeQuoteTemplates.value.map(template => ({
    value: template.id,
    label: template.name,
  })),
]);
const messageOptions = computed(() => [
  { value: '', label: copy.noTemplate },
  ...templates.value.map((template, index) => ({
    value: index,
    label: templateLabel(template),
  })),
]);
const currency = value =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value || 0)
  );
const itemTotal = item =>
  (Number(item.quantity) > 0 ? Number(item.quantity) : 1) *
  Number(item.unit_price || 0);
const previewGreeting = computed(
  () =>
    `Olá ${quotePayload.value?.recipient_name || 'tudo bem'}! Confira seu orçamento abaixo.`
);

const findBodyComponent = template =>
  (Array.isArray(template?.components) ? template.components : []).find(
    component => component.type === 'BODY'
  );

const firstBodyVariable = template => {
  const body = findBodyComponent(template)?.text || template?.body || '';
  const match = body.match(/\{\{\s*([\w.]+)\s*\}\}/);
  return match?.[1];
};

const buildTemplateParams = template => {
  if (!template || !contract.value) return null;

  const bodyVariable = firstBodyVariable(template);
  const processedParams = bodyVariable
    ? { body: { [bodyVariable]: contract.value.public_url } }
    : {};

  return {
    name: template.name || template.friendly_name,
    namespace: template.namespace,
    language: template.language || 'pt_BR',
    processed_params: processedParams,
  };
};

const resetEmailFields = () => {
  contactEmailRequestId += 1;
  contactEmail.value = '';
  isLoadingContactEmail.value = false;
  form.emailMode = 'contact';
  form.quoteAttachmentFormat = '';
  form.toEmail = '';
  form.ccEmails = '';
};

const loadContactEmail = async contactId => {
  if (!contactId) {
    form.emailMode = 'manual';
    return;
  }
  contactEmailRequestId += 1;
  const requestId = contactEmailRequestId;
  isLoadingContactEmail.value = true;
  try {
    const { data } = await ContactAPI.show(contactId);
    if (requestId !== contactEmailRequestId) return;
    contactEmail.value = String(data?.payload?.email || '')
      .trim()
      .toLowerCase();
  } catch {
    if (requestId !== contactEmailRequestId) return;
    contactEmail.value = '';
  } finally {
    if (requestId === contactEmailRequestId) {
      isLoadingContactEmail.value = false;
      if (!contactEmail.value) form.emailMode = 'manual';
    }
  }
};

const open = nextContract => {
  contract.value = nextContract;
  resetEmailFields();
  quotePayload.value = null;
  form.inboxId = '';
  form.templateIndex = '';
  form.quoteTemplateId = '';
  form.documentKind = 'quote';
  form.receiptFormat = 'pdf';
  form.receivedAmount = '';
  form.receivedOn = '';
  form.paymentMethod = '';
  form.paymentConfirmed = false;
  dialogRef.value?.open();
};

const openQuote = nextQuotePayload => {
  quotePayload.value = nextQuotePayload;
  resetEmailFields();
  loadContactEmail(nextQuotePayload?.contact_id);
  contract.value = null;
  form.inboxId = '';
  form.templateIndex = '';
  form.quoteTemplateId = '';
  form.documentKind = 'quote';
  form.receiptFormat = 'pdf';
  form.receivedAmount = '';
  form.receivedOn = '';
  form.paymentMethod = paymentMethodOptions.value.some(
    option => option.value === nextQuotePayload.payment_method
  )
    ? nextQuotePayload.payment_method
    : '';
  form.paymentConfirmed = false;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleInboxChange = () => {
  form.templateIndex = '';
};

const handleConfirm = () => {
  if (isConfirmDisabled.value) return;

  if (isQuoteMode.value) {
    emit('sendQuote', {
      ...quotePayload.value,
      inbox_id: form.inboxId,
      quote_template_id: isReceipt.value ? '' : form.quoteTemplateId,
      document_kind: form.documentKind,
      quote_attachment_format: isReceipt.value
        ? ''
        : form.quoteAttachmentFormat,
      ...(isEmailInbox.value
        ? { to_email: emailRecipient.value, cc_emails: ccList.value.join(',') }
        : {}),
      ...(isReceipt.value
        ? {
            receipt_format: form.receiptFormat,
            received_amount: form.receivedAmount,
            received_on: form.receivedOn,
            payment_method: form.paymentMethod,
            payment_confirmed: form.paymentConfirmed,
          }
        : {}),
    });
    return;
  }

  emit('send', {
    contract: contract.value,
    payload: {
      inbox_id: form.inboxId,
      template_params: buildTemplateParams(selectedTemplate.value),
    },
  });
};

const handleGenerateQuotePdf = () => {
  if (!quotePayload.value) return;

  emit('generateQuotePdf', {
    ...quotePayload.value,
    quote_template_id: form.quoteTemplateId,
  });
};

const copySignatureLink = async () => {
  const signatureLink = quotePayload.value?.public_url;
  if (!signatureLink) return;

  try {
    await navigator.clipboard.writeText(signatureLink);
    useAlert('Link de assinatura copiado.');
  } catch {
    useAlert('Não foi possível copiar o link de assinatura.');
  }
};

defineExpose({ open, openQuote, close });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Dialog
    ref="dialogRef"
    solid
    width="3xl"
    overflow-y-auto
    sticky-header-footer
    :title="title"
    :description="description"
    :cancel-button-label="copy.cancel"
    :confirm-button-label="copy.confirm"
    :disable-confirm-button="isConfirmDisabled"
    @confirm="handleConfirm"
  >
    <div
      v-if="isQuoteMode"
      class="mb-5 flex flex-wrap gap-2 rounded-lg bg-n-solid-1 p-1.5"
    >
      <Button
        :label="copy.quote"
        :variant="!isReceipt ? 'faded' : 'ghost'"
        color="slate"
        type="button"
        :disabled="isSending"
        @click="form.documentKind = 'quote'"
      />
      <Button
        :label="copy.receipt"
        :variant="isReceipt ? 'faded' : 'ghost'"
        color="slate"
        type="button"
        :disabled="isSending"
        @click="form.documentKind = 'receipt'"
      />
    </div>
    <div class="grid gap-5 md:grid-cols-2">
      <section class="min-w-0 space-y-4">
        <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ copy.destination }}
        </h3>
        <p
          v-if="!inboxes.length"
          class="rounded-lg bg-n-solid-1 p-3 text-sm text-n-slate-11"
        >
          {{ copy.noInbox }}
        </p>
        <div v-else class="space-y-2">
          <label class="text-xs font-medium text-n-slate-11">{{
            copy.inbox
          }}</label>
          <ComboBox
            v-model="form.inboxId"
            :options="inboxOptions"
            :placeholder="copy.selectInbox"
            :disabled="isSending"
            teleport-to-body
            @update:model-value="handleInboxChange"
          />
        </div>
        <div
          v-if="isEmailInbox"
          class="space-y-3 rounded-xl border border-n-weak p-4"
        >
          <div
            class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <Icon icon="i-hugeicons-mail-01" class="size-4" />
            {{ copy.emailRecipient }}
          </div>
          <p
            v-if="isLoadingContactEmail"
            class="mb-0 text-xs text-n-slate-11"
            role="status"
          >
            {{ copy.loadingContactEmail }}
          </p>
          <template v-else>
            <div class="grid gap-2 sm:grid-cols-2" role="radiogroup">
              <button
                v-for="option in emailModeOptions"
                :key="option.value"
                type="button"
                role="radio"
                :aria-checked="form.emailMode === option.value"
                :disabled="isSending || option.disabled"
                class="flex min-w-0 flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                :class="
                  form.emailMode === option.value
                    ? 'border-n-brand bg-n-brand/10'
                    : 'border-n-weak hover:border-n-slate-7'
                "
                @click="form.emailMode = option.value"
              >
                <span class="text-sm font-medium text-n-slate-12">{{
                  option.label
                }}</span>
                <span
                  v-if="option.detail"
                  class="w-full truncate text-xs text-n-slate-11"
                  :title="option.detail"
                  >{{ option.detail }}</span
                >
              </button>
            </div>
            <p v-if="!contactEmail" class="mb-0 text-xs text-n-slate-11">
              {{ copy.noContactEmail }}
            </p>
            <Input
              v-if="form.emailMode === 'manual' || !contactEmail"
              v-model="form.toEmail"
              type="email"
              :label="copy.emailAddress"
              :placeholder="copy.emailPlaceholder"
              :disabled="isSending"
              :message="
                form.toEmail && isRecipientEmailInvalid ? copy.invalidEmail : ''
              "
              :message-type="
                form.toEmail && isRecipientEmailInvalid ? 'error' : 'info'
              "
            />
          </template>
          <Input
            v-model="form.ccEmails"
            :label="copy.ccEmails"
            :placeholder="copy.ccPlaceholder"
            :disabled="isSending"
            :message="ccError || copy.ccHint"
            :message-type="ccError ? 'error' : 'info'"
          />
          <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
            {{ copy.emailConversationHint }}
          </p>
        </div>
        <div v-if="isQuoteMode && !isReceipt" class="space-y-2">
          <label class="text-xs font-medium text-n-slate-11">{{
            copy.quoteTemplate
          }}</label>
          <ComboBox
            v-model="form.quoteTemplateId"
            :options="quoteOptions"
            :disabled="isSending"
            teleport-to-body
          />
          <p
            v-if="!form.quoteTemplateId"
            class="text-xs leading-relaxed text-n-slate-11"
          >
            {{ copy.defaultDescription }}
          </p>
        </div>
        <div v-if="isQuoteMode && !isReceipt" class="space-y-2">
          <span class="block text-xs font-medium text-n-slate-11">{{
            copy.sendFormat
          }}</span>
          <div
            class="grid gap-2 sm:grid-cols-3"
            role="radiogroup"
            :aria-label="copy.sendFormat"
          >
            <button
              v-for="option in quoteFormatOptions"
              :key="option.value || 'text'"
              type="button"
              role="radio"
              :aria-checked="form.quoteAttachmentFormat === option.value"
              :disabled="isSending"
              class="flex min-w-0 flex-col items-start gap-1 rounded-lg border px-3 py-2 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              :class="
                form.quoteAttachmentFormat === option.value
                  ? 'border-n-brand bg-n-brand/10'
                  : 'border-n-weak hover:border-n-slate-7'
              "
              @click="form.quoteAttachmentFormat = option.value"
            >
              <Icon :icon="option.icon" class="size-4 text-n-slate-11" />
              <span class="text-sm font-medium text-n-slate-12">{{
                option.label
              }}</span>
              <span class="text-xs text-n-slate-11">{{ option.detail }}</span>
            </button>
          </div>
          <p
            v-if="form.quoteAttachmentFormat"
            class="mb-0 text-xs leading-relaxed text-n-slate-11"
          >
            {{ copy.sendFormatHint }}
          </p>
        </div>
        <div
          v-if="!isQuoteMode && selectedInbox && templates.length"
          class="space-y-2"
        >
          <label class="text-xs font-medium text-n-slate-11">{{
            copy.template
          }}</label>
          <ComboBox
            v-model="form.templateIndex"
            :options="messageOptions"
            :disabled="isSending"
            teleport-to-body
          />
        </div>
        <div v-if="isReceipt" class="space-y-3">
          <CurrencyInput
            v-model="form.receivedAmount"
            :label="copy.receivedAmount"
            :disabled="isSending"
          />
          <Input
            v-model="form.receivedOn"
            :label="copy.receivedOn"
            type="date"
            :disabled="isSending"
          />
          <div class="space-y-2">
            <label class="text-xs font-medium text-n-slate-11">{{
              copy.paymentMethod
            }}</label>
            <ComboBox
              v-model="form.paymentMethod"
              :options="paymentMethodOptions"
              placeholder="Selecione a forma de pagamento"
              :disabled="isSending"
              teleport-to-body
            />
          </div>
          <label class="flex items-start gap-2 text-xs text-n-slate-11"
            ><Checkbox
              v-model="form.paymentConfirmed"
              :disabled="isSending"
            />{{ copy.confirmPayment }}</label
          >
        </div>
        <div class="rounded-xl border border-n-weak p-4">
          <div
            class="mb-3 flex items-center gap-2 text-xs font-medium text-n-slate-11"
          >
            <Icon icon="i-hugeicons-user-circle" class="size-4" />{{
              copy.recipient
            }}
          </div>
          <strong class="block break-words text-sm text-n-slate-12">{{
            quotePayload?.recipient_name ||
            contract?.recipient_name ||
            copy.customer
          }}</strong>
          <span class="mt-1 block break-words text-xs text-n-slate-11">{{
            quotePayload?.recipient_phone ||
            contract?.recipient_phone ||
            'Telefone não informado'
          }}</span>
          <span
            v-if="quotePayload?.recipient_cpf"
            class="mt-2 block text-xs text-n-slate-11"
            >{{ copy.document }}: {{ quotePayload.recipient_cpf }}</span
          >
        </div>
        <div
          v-if="isQuoteMode && !isReceipt && !form.quoteTemplateId"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4"
        >
          <span class="mb-2 block text-xs font-semibold text-n-slate-11">{{
            copy.preview
          }}</span>
          <p class="mb-2 text-sm text-n-slate-12">{{ previewGreeting }}</p>
          <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
            Quer ajustar algum item ou tirar uma dúvida? Responda esta mensagem.
            Estamos à disposição para ajudar.
          </p>
        </div>
      </section>
      <section class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <h3 class="mb-4 text-sm font-semibold text-n-slate-12">
          {{
            isReceipt
              ? copy.receipt
              : isQuoteMode
                ? copy.review
                : copy.contractTitle
          }}
        </h3>
        <div
          v-if="isReceipt"
          class="overflow-hidden rounded-xl border border-n-weak bg-n-background"
        >
          <div class="space-y-5 p-5">
            <div
              class="flex items-center justify-between gap-3 border-b border-dashed border-n-weak pb-4"
            >
              <Icon
                icon="i-lucide-receipt-text"
                class="size-7 text-n-slate-11"
              />
              <span class="text-xs uppercase tracking-widest text-n-slate-11"
                >Prévia do recibo</span
              >
            </div>
            <div>
              <span class="text-xs text-n-slate-11">Recebemos de</span
              ><strong class="mt-1 block text-sm text-n-slate-12">{{
                quotePayload?.recipient_name ||
                contract?.recipient_name ||
                copy.customer
              }}</strong>
            </div>
            <div class="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span class="block text-xs text-n-slate-11"
                  >Data do recebimento</span
                ><span class="mt-1 block">{{
                  form.receivedOn
                    ? form.receivedOn.split('-').reverse().join('/')
                    : 'A informar'
                }}</span>
              </div>
              <div>
                <span class="block text-xs text-n-slate-11"
                  >Forma de pagamento</span
                ><span class="mt-1 block">{{
                  form.paymentMethod || 'A informar'
                }}</span>
              </div>
            </div>
            <p class="mb-0 text-xs leading-relaxed text-n-slate-11">
              Este recibo corresponde somente ao valor informado e não declara
              quitação de outros valores.
            </p>
          </div>
          <div
            class="border-t-2 border-dashed border-[#e4be4d] bg-[#fff0b3] px-5 py-5 text-[#503900]"
          >
            <span class="text-xs font-medium uppercase tracking-wide"
              >Valor recebido</span
            >
            <strong class="mt-1 block text-3xl">{{
              currency(form.receivedAmount)
            }}</strong>
            <p class="mb-0 mt-3 text-xs">
              O cliente receberá o recibo em
              {{ form.receiptFormat === 'png' ? 'imagem (PNG)' : 'PDF' }}.
              Guarde este comprovante.
            </p>
          </div>
        </div>
        <template v-else-if="isQuoteMode">
          <div class="mb-4 rounded-xl border border-n-weak bg-n-solid-1 p-4">
            <span class="block text-xs text-n-slate-11">{{ copy.total }}</span>
            <strong class="mt-1 block text-2xl font-semibold text-n-slate-12">{{
              formattedQuoteTotal
            }}</strong>
            <span class="mt-1 block text-xs text-n-slate-11"
              >{{ quoteItems.length }}
              {{ quoteItems.length === 1 ? 'item' : 'itens' }}</span
            >
          </div>
          <details open class="text-sm">
            <summary class="cursor-pointer font-medium text-n-slate-12">
              {{ copy.reviewItems }}
            </summary>
            <ul class="mt-3 max-h-56 space-y-3 overflow-y-auto p-0">
              <li
                v-for="(item, index) in quoteItems"
                :key="index"
                class="flex items-start justify-between gap-3 border-b border-n-weak pb-3 last:border-0"
              >
                <div class="min-w-0">
                  <span class="block break-words text-sm text-n-slate-12">{{
                    item.product_name || 'Item'
                  }}</span
                  ><span class="text-xs text-n-slate-11"
                    >{{ Number(item.quantity) > 0 ? item.quantity : 1 }} ×
                    {{ currency(item.unit_price) }}</span
                  >
                </div>
                <strong class="shrink-0 text-sm text-n-slate-12">{{
                  currency(itemTotal(item))
                }}</strong>
              </li>
            </ul>
          </details>
        </template>
        <p class="mb-0 mt-4 text-xs leading-relaxed text-n-slate-11">
          {{ isReceipt ? copy.receiptHint : copy.sendHint }}
        </p>
      </section>
    </div>
    <template #footer>
      <div class="flex w-full flex-wrap items-center justify-between gap-3">
        <div class="flex flex-wrap gap-2">
          <template v-if="isReceipt">
            <Button
              variant="ghost"
              color="slate"
              icon="i-hugeicons-file-download"
              label="Gerar PDF"
              type="button"
              :disabled="isSending || isReceiptInvalid"
              @click="handleGenerateReceipt('pdf')"
            />
            <Button
              variant="ghost"
              color="slate"
              icon="i-hugeicons-image-01"
              label="Gerar imagem"
              type="button"
              :disabled="isSending || isReceiptInvalid"
              @click="handleGenerateReceipt('png')"
            />
            <label class="flex items-center gap-2 text-xs text-n-slate-11">
              Enviar como
              <select
                v-model="form.receiptFormat"
                :disabled="isSending"
                class="mb-0 w-auto text-xs"
              >
                <option value="pdf">PDF</option>
                <option value="png">Imagem (PNG)</option>
              </select>
            </label>
          </template>
          <Button
            v-if="isQuoteMode && !isReceipt"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-file-download"
            :label="copy.generatePdf"
            :disabled="isSending"
            type="button"
            @click="handleGenerateQuotePdf"
          />
          <Button
            v-if="isQuoteMode && !isReceipt && quotePayload.public_url"
            variant="ghost"
            color="slate"
            icon="i-hugeicons-link-01"
            :label="copy.copySignatureLink"
            :disabled="isSending"
            type="button"
            @click="copySignatureLink"
          />
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            variant="faded"
            color="slate"
            :label="copy.cancel"
            :disabled="isSending"
            type="button"
            @click="close"
          />
          <Button
            color="blue"
            icon="i-hugeicons-sent"
            :label="title"
            :disabled="isConfirmDisabled"
            :is-loading="isSending"
            type="submit"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
